import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import Home from '@mui/icons-material/Home';
import config from '../../../config';
import { FloatingLegend } from './FloatingLegend';
import { Layers } from './layer-selector/Layers';
import { useLayers } from './use-layers';
import { useMapLoaded } from './use-map-loaded';
import { useMapRefresh } from './use-map-refresh';
import MapControls from './MapControls';
import { MyLocationControl } from './MyLocationControl';
import { Spinner } from '../../Spinner';

const defaultProps = {
  selectedFeatures: {},
  filters: { growerId: '' },
  layerVisibilities: {},
  minZoom: 4,
  center: { lat: 39, lng: -98 },
};

function GoogleMap({
  apiRef,
  hideLayers,
  bounds,
  center = defaultProps.center,
  filters: filtersConfig = defaultProps.filters,
  layerVisibilities = defaultProps.layerVisibilities,
  layers: layersConfig,
  markers,
  minZoom = defaultProps.minZoom,
  onClick,
  onHomeClick,
  onLoad,
  selectedFeatures = defaultProps.selectedFeatures,
  working,
  zoom,
  zoomControl = true,
  mapTypeControl = true,
  myLocationControl = false,
  getControls,
  children,
  pkRemovalEnabled,
}) {
  const rootContainer = useRef();
  const mapContainer = useRef();
  const [map, setMap] = useState();
  const [, setMapMarkers] = useState([]);
  const myLocationMarker = useRef();
  const mapLoaded = useMapLoaded(map);
  const { refreshAt } = useMapRefresh();
  const filters = useMemo(
    () => ({ ...filtersConfig, _: refreshAt }),
    [refreshAt, filtersConfig],
  );

  const layers = useLayers({
    config: layersConfig,
    filters,
    layerVisibilities,
    map,
    onClick: onLayerClick,
    selectedFeatures,
    pkRemovalEnabled,
  });

  useEffect(mapEffect, [
    map,
    onLoad,
    minZoom,
    zoom,
    center,
    mapTypeControl,
    zoomControl,
  ]);
  useEffect(mapLoadedEffect, [map, mapLoaded, onLoad]);
  useEffect(markersEffect, [map, mapLoaded, markers]);
  useEffect(zoomToBoundsEffect, [map, bounds, mapLoaded]);
  useEffect(homeButtonEffect, [map, mapLoaded, onHomeClick]);

  if (apiRef) {
    apiRef.current = {
      showLayer: (id) =>
        layers.updateConfig(id, { visible: true, hidden: false }),
      hideLayer: (id) =>
        layers.updateConfig(id, { visible: false, hidden: true }),
    };
  }

  return (
    <Box
      ref={rootContainer}
      sx={{
        height: '100%',
        position: 'relative',
        '& .editing .gm-bundled-control, .editing + .map-loading': {
          top: '54px!important',
        },
        'button[title="Undo last edit"]': {
          display: 'none',
        },
      }}
    >
      {buildControls()}
      {buildFloatingLegend()}
      <Box ref={mapContainer} sx={{ height: '100%', borderRadius: 1 }} />
      {layers?.areAnyLoading ? (
        <LinearProgress
          className="map-loading"
          sx={{ position: 'absolute', top: 0, width: '100%' }}
        />
      ) : null}
      {working ? (
        <Backdrop
          open
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}
    </Box>
  );

  function onLayerClick(id, value, properties, latLng) {
    return /-(grid|cloud)/.test(id)
      ? showGridCellInfo(properties, latLng)
      : onClick?.(id, value);
  }

  function showGridCellInfo(properties, latLng) {
    const { id, m2, acres, resolution, meta, status } = properties;
    const number = (n, digits = 0) =>
      Number.isNaN(Number(n))
        ? n
        : n.toLocaleString('en-US', { maximumFractionDigits: digits });

    const buildRow = (key, value) =>
      `<tr><td><strong>${key}</strong></td><td>${number(value)}</td></tr>`;

    const buildRows = (o) =>
      Object.entries(o)
        .map(([key, value]) => buildRow(key, number(value, 4)))
        .join('');

    const rows = [
      buildRow('Cell Id', id),
      resolution ? buildRow('Resolution', resolution) : '',
      status ? buildRow('Status', status) : '',
      acres ? buildRow('Acres', number(acres)) : '',
      m2 ? buildRow('Meters²', number(m2)) : '',
      meta ? buildRows(meta) : '',
    ];

    const info = new window.google.maps.InfoWindow({
      position: latLng,
      content: `<table>${rows.join('')}</table>`,
    });

    info.open({ map });
  }

  function mapEffect() {
    if (!map) {
      const mapInstance = new window.google.maps.Map(mapContainer.current, {
        mapTypeId: import.meta.env.MODE === 'production' ? 'hybrid' : 'roadmap',
        mapTypeControl,
        ...(mapTypeControl && {
          mapTypeControlOptions: {
            style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: window.google.maps.ControlPosition.LEFT_BOTTOM,
          },
        }),
        center,
        minZoom,
        cameraControl: false,
        zoom: zoom || minZoom,
        zoomControl,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.LEFT_TOP,
        },
        scaleControl: true,
        streetViewControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        fullscreenControl: false,
        tilt: 0,
      });

      setMap(mapInstance);
    }
  }

  function mapLoadedEffect() {
    if (mapLoaded && onLoad) onLoad(map);
  }

  function markersEffect() {
    if (mapLoaded && markers) {
      setMapMarkers((current) => {
        current.forEach((marker) => marker.setMap(null));
        return markers.map(
          (marker) => new window.google.maps.Marker({ ...marker, map }),
        );
      });
    }
  }

  function zoomToBoundsEffect() {
    if (mapLoaded && bounds) {
      const [west, south, east, north] = bounds;
      map.fitBounds({ west, south, east, north });
    }
  }

  function homeButtonEffect() {
    if (!mapLoaded || !onHomeClick) return;

    const { LEFT_TOP } = window.google.maps.ControlPosition;

    const button = (
      <Button
        title="Reset Zoom"
        variant="contained"
        color="inherit"
        onClick={onHomeClick}
        sx={{
          pointerEvents: 'auto',
          minWidth: 0,
          width: 40,
          bgcolor: 'white',
          ml: '10px',
        }}
      >
        <Home
          fontSize="medium"
          sx={{ color: '#666', '&:hover': { color: '#333' } }}
        />
      </Button>
    );

    const controlDiv = document.createElement('div');
    const controlRoot = createRoot(controlDiv);
    controlRoot.render(button);
    map.controls[LEFT_TOP].push(controlDiv);

    return () => {
      controlRoot.unmount();
      map.controls[LEFT_TOP].clear();
    };
  }

  function buildFloatingLegend() {
    const layer = layers?.find((l) => l.floatingLegend);
    return layer ? <FloatingLegend layer={layer} /> : null;
  }

  function buildControls() {
    if (!mapLoaded) return null;
    return (
      <MapControls
        top={React.Children.map(children, (child) =>
          React.cloneElement(child, { layers, map }),
        )}
      >
        {getControls && getControls()}
        {buildMyLocationControl()}
        {buildLayersControl()}
      </MapControls>
    );
  }
  function buildMyLocationControl() {
    return myLocationControl ? (
      <MyLocationControl map={map} markerRef={myLocationMarker} />
    ) : null;
  }

  function buildLayersControl() {
    if (!layers?.length || hideLayers) return null;
    return <Layers layers={layers} containerRef={rootContainer} />;
  }
}

export default function MapWrapper(props) {
  return (
    <Wrapper
      apiKey={config.googleMapsApiKey}
      render={(status) => {
        if (status === Status.LOADING) return <Spinner />;
        if (status === Status.FAILURE) return <h3>Unable to load the map</h3>;
        return <GoogleMap {...props} />;
      }}
    />
  );
}
